declare var App: any;

export interface IStation {
    elevation: number;
    latitude: number;
    longitude: number;
    id: string;
    name: string;
    timeZone: string;
}

export interface IStationRecord {
    date: string;
    day: string;
    interval: number;
    stationId: string;
    timeOffset: string;
    timestamp: string;
    values: any;
}

export interface IMetadata {
    accumulation: string;
    description: string;
    interpretation: string;
    key: string;
    longName: string;
    shortName: string;
    scale: number;
    units: string;
}

export interface IStationMarker extends google.maps.OverlayView {
    station: IStation;
    record: IStationRecord;
    getPosition(): google.maps.LatLng;
    setField(field: IMetadata): void;

    select(): void;
    deselect(): void;

    isOld(): boolean;
}




/** The main object controlling a WWG map report map */
export class MapReport {
    static readonly mapContainerClassName = "map-report-map";

    map: google.maps.Map;
    container: HTMLElement;
    detailWindow: DetailWindow;
    markers: Array<IStationMarker> = new Array<IStationMarker>();
    asOfTitle: HTMLElement;
    controls: CustomMapControls;
    lastRefresh: Date;

    constructor(container: HTMLElement) {
        extendGoogleMaps();

        this.container = container;
        this.setupMap();

        this.asOfTitle = document.getElementById(`asof-${container.id}`);
    }

    /**
     * Event handler for when a user changes the map type
     * @param map The google map object
     */
    mapTypeChangeHandler(map: google.maps.Map) {
        var mapType = map.getMapTypeId();
        map.getDiv().className = "map-report-map " + mapType.toString();
    }

    /**
     * Queries data and does initial map setup
     * @param container The container the map will be added to
     */
    setupMap() {
        var self = this;

        try {
            App.blockUI({
                target: self.container,
                animate: true
            });
        } catch (e) {
            if (console) console.log(e);
        }

        this.getData();

    }


    public refreshData(): void {
        if (this.lastRefresh) {
            var now = new Date();
            var secondsSinceLast = (<any>now - <any>this.lastRefresh) / 1000;

            //Only refresh up to once every X seconds, in case someone jams the refresh button
            if (secondsSinceLast < 5) return;
        }

        //Set the last refresh even though it hasn't completed so subsequence refresh mashing doesn't pull the data multiple times
        this.lastRefresh = new Date();
        this.getData();
    }


    private getData() {
        var self = this;
        var config = (<any>window).mapReportData[self.container.id];

        $.ajax({
            url: "/mapapi/query",
            method: "post",
            data: config,
            success(data: any, status: string, xhr: JQueryXHR) {
                try {
                    self.setData(data);
                } catch (e) {
                    self.handleFailure(e);
                }
            },
            error(xhr: JQueryXHR, status: string, error: string) {
                self.handleFailure(error);
            },
            complete(xhr: JQueryXHR, status: string) {
                try {
                    App.unblockUI(self.container);
                } catch (e) {
                    if (window.console) console.log(e);
                }
            }
        });
    }


    private handleFailure(error: any, message?: string): void {
        if (window.console) {
            console.log(error);
        }

        var div = document.createElement("div");
        div.className = "map-error";

        var inner = document.createElement("div");

        var title = document.createElement("div");
        title.className = "map-error-title";
        title.innerText = "Sorry, an error occurred";

        var msgDiv = document.createElement("div");
        msgDiv.className = "map-error-msg";
        msgDiv.innerText = message || "The map could not be loaded";

        inner.appendChild(title);
        inner.appendChild(msgDiv);
        div.appendChild(inner);

        this.container.innerHTML = "";
        this.container.appendChild(div);
    }

    /**
     * Creates the google maps object in the specified container
     * @param container The html element map container
     */
    private createMap(container: HTMLElement) {
        var self = this;

        var map = new google.maps.Map(container,
            {
                zoom: 4,
                center: new google.maps.LatLng(39.5, -98.35),
                mapTypeId: 'terrain',
                streetViewControl: false,
                clickableIcons: false, //Turns off POI click icons/windows
                mapTypeControl: false,
                /*mapTypeControlOptions: {
                    style: google.maps.MapTypeControlStyle.DROPDOWN_MENU
                }*/
            });

        //var radar = this.createAerisLayer();
        //map.overlayMapTypes.push(radar);

        map.addListener("maptypeid_changed", () => { self.mapTypeChangeHandler(map); });
        return map;
    }


    private createAerisLayer(): google.maps.ImageMapType {
        const id = "p9DrDWwOWMOehu1ocLMrW";
        const key = "yoScmo7d9FG7U4lFN7mhy0VDtIBuMutzq8lmTfUW";

        var radar = new google.maps.ImageMapType({
            getTileUrl: (coord, zoom) => `https://maps.aerisapi.com/${id}_${key}/radar/${zoom}/${coord.x}/${coord.y}/current.png`,
            tileSize: new google.maps.Size(256, 256)
        });

        return radar;
    }


    /**
     * Uses the data response from the API to build up the map elements
     * @param data The data returned from the API
     * @param map The google map object
     */
    private setData(data: any) {
        var self = this;
        var isRefresh = false;
        self.lastRefresh = new Date();

        if (!self.map) {

            //Create the map if this is the first time data is loaded
            self.map = self.createMap(self.container);

            //Call the map changed handler immediately to handle its initial map type
            self.mapTypeChangeHandler(self.map);
        } else {
            isRefresh = true;
        }


        var previousDetailWindowStationId: string = null;

        //Create or update the detail window
        if (!self.detailWindow) {
            self.detailWindow = new DetailWindow(self.map,
                google.maps.ControlPosition.LEFT_CENTER,
                data.metadata,
                data.fields,
                self);
        } else {
            if (self.detailWindow.marker) {
                //Record the station id of the current open detail window, so it can be opened for its new marker later
                previousDetailWindowStationId = self.detailWindow.marker.station.id;
            }
            self.detailWindow.close();
            self.detailWindow.fields = data.fields;
            self.detailWindow.metadata = data.metadata;
        }


        //Update the as of label
        if (data.asof) {
            self.asOfTitle.innerHTML = `<span class="text-muted">as of</span> ${data.asof}`;
        } else {
            self.asOfTitle.innerText = "";
        }


        //Remove existing markers
        if (self.markers && self.markers.length > 0) {
            for (let marker of self.markers) {
                marker.setMap(null);
            }

            self.markers = new Array<IStationMarker>();
        }

        //Add or update custom controls
        if (!self.controls) {
            self.controls = new CustomMapControls(self.map,
                google.maps.ControlPosition.TOP_RIGHT,
                data.metadata,
                data.fields,
                self);
        } else {
            self.controls.setFields(data.metadata, data.fields);
        }

        var bounds = new google.maps.LatLngBounds();

        //Add markers for every station in the data set
        for (let key in data.stations) {
            if (data.stations.hasOwnProperty(key)) {
                let station: IStation = data.stations[key];

                //Lat & Lng required to place marker on the map
                if (!station.latitude || !station.longitude) continue;

                let record: IStationRecord = data.records[key];


                //Its possible there are no records yet for the station. If not, skip it
                if (!record || !record.values) {
                    continue;
                };

                //The first field in the set is the default field
                let field: IMetadata = self.controls.currentField;

                let age: number = data.ages[key];
                var latLng = new google.maps.LatLng(station.latitude, station.longitude);

                var marker = new StationMarker(latLng, self.map, station, record, self, age);
                marker.setField(field);

                self.markers.push(marker);

                if (isRefresh && previousDetailWindowStationId && station.id === previousDetailWindowStationId) {
                    //Open the detail window for the new marker for the station that had its detail window open before the refresh, if any
                    self.detailWindow.show(marker);
                    marker.select();
                }

                bounds.extend(latLng);
            }
        }

        if (!isRefresh) {
            //Only set bounds on initial data load
            //So it doesn't pan on refresh
            self.map.fitBounds(bounds);
        }
    }

    selectStation(marker: IStationMarker) {
        for (let marker of this.markers) {
            marker.deselect();
        }

        marker.select();
        this.detailWindow.show(marker);
    }
}


let StationMarker: { new(latlng: google.maps.LatLng, map: google.maps.Map, station: IStation, record: IStationRecord, report: MapReport, age: number, args?: any): IStationMarker };



function extendGoogleMaps() {
    StationMarker = class StationMarker extends google.maps.OverlayView {
        private static readonly baseClassName: string = "marker station-marker";
        private static readonly oldAgeCutoff: number = 65;

        private container: HTMLDivElement;
        private barb: HTMLDivElement;
        private label: HTMLSpanElement;

        private latlng: google.maps.LatLng;
        private windDir: number;
        private args: any;
        public station: IStation;
        public record: IStationRecord;
        public report: MapReport;
        public age: number;
        private pane: Element;
        private showingGust: boolean = false;

        private isAdded: boolean = false;

        constructor(latlng: google.maps.LatLng, map: google.maps.Map, station: IStation, record: IStationRecord, report: MapReport, age: number, args?: any) {
            super();
            this.latlng = latlng;
            this.args = args;
            this.station = station;
            this.record = record;
            this.report = report;
            this.age = age;

            this.barb = document.createElement("div");
            this.label = document.createElement("span");

            this.setMap(map);

            this.container = document.createElement("div");
            this.container.className = StationMarker.baseClassName;
        }

        /** Called by google maps when the map is ready for the marker overlay to be attached.
         * See: https://developers.google.com/maps/documentation/javascript/customoverlays#add
         */
        override onAdd(): void {
            var self = this;

            if (!self.isAdded) {
                self.isAdded = true;

                this.container.title = this.station.name;

                if (self.isOld()) {
                    self.container.className += " station-marker-old";
                }

                google.maps.event.addDomListener(self.container, "click", (event: Event) => {
                    google.maps.event.trigger(self, "click");
                    self.report.selectStation(this);
                }, true);

                this.setWind(this.showingGust);

                self.container.appendChild(self.barb);
                self.container.appendChild(self.label);

                //Add the div to the pane
                self.pane = self.getPanes().overlayMouseTarget;
                self.pane.appendChild(self.container);

                //UNCOMMENT FOR ROTATION TEST
                /*window.setInterval(() => {
                    var dir = this.windDir + 1;
                    if (dir >= 360) dir = 0;
                    self.setWindDirection(dir);
                }, 16);*/
            }
        }

        /** Called by google maps when the overlay is removed from the map */
        override onRemove(): void {
            if (this.container && this.pane) {
                this.pane.removeChild(this.container);
                //this.container = null;
                this.pane = null;
                //this.isAdded = false;
            }
        }

        /** Called by google map when it needs to draw the overlay on the map, including when first added
         * See: https://developers.google.com/maps/documentation/javascript/customoverlays#draw
         */
        override draw(): void {
            var point = this.getProjection().fromLatLngToDivPixel(this.latlng);
            if (point) {
                this.container.style.left = point.x + "px";
                this.container.style.top = point.y + "px";
            }
        }

        /** Returns the Lat/Lng position of the marker */
        getPosition(): google.maps.LatLng {
            return this.latlng;
        }


        private setWind(useGust: boolean): void {
            var self = this;

            let barb = self.barb;
            let label = self.label;

            var windDir = self.record.values.WindDir;
            var windSpeed = self.record.values.WindSpeed;

            this.showingGust = useGust;
            if (useGust) {
                //Use gust if requested and available available

                var gustSpeed = self.record.values.WindMax;
                var gustDir = self.record.values.WindMaxDir;

                if (gustSpeed != null && gustDir != null) {
                    windDir = gustDir;
                    windSpeed = gustSpeed;
                }

                this.showingGust = true;
            }

            label.className = "marker-label";

            if (windDir == null || windSpeed == null) {
                barb.className = "marker-point";
                barb.innerHTML = SvgBarbGenerator.point();
            } else {
                if (windSpeed < 0.01) {
                    barb.className = "marker-calm";
                    barb.innerHTML = SvgBarbGenerator.calm();
                } else {
                    //NOTE: WWG wants to use MPH for barbs, despite the standard for knots
                    //var speed = this.mphToKnots(windSpeed);

                    var speed = windSpeed + 2.5;

                    //IMPORTANT
                    //We are adding 2.5 to the speed so that in effect rounds to the closet barb. 
                    //This way the 5 unit barb will be used at speeds starting at 2.5. And the 10 will be used at 7.5
                    //This is effectively rounding to the closest barb

                    barb.className = "marker-barb";
                    barb.innerHTML = new SvgBarbGenerator(speed).build();
                    self.setWindDirection(windDir);
                }
            }
        }


        /**
         * Updates the UI to set the rotation of the marker to match a wind direction degree value
         * @param degrees The wind direction, in degrees
         */
        private setWindDirection(degrees: number): void {
            this.windDir = degrees;
            if (!this.container || !this.label || !this.barb) return;

            //Calculate translateX percentage based on how close the degrees are to 90 or 270
            //The closer to 90 or 270, the closer to -50% and +50% the value will be
            //This helps keep the label oriented to the barb better despite its shape

            var b = this.windDir > 180
                ? this.windDir - 180
                : this.windDir;

            var tx = 50 - Math.abs(90 - b) / 1.8;
            if (this.windDir <= 180) tx = tx * -1;

            this.container.style.transform = "rotate(" + this.windDir + "deg)";
            this.label.style.transform = "translateX(-50%) rotate(-" + this.windDir + "deg) translateX(" + tx + "%)";
        }

        /**
         * Sets the field that should be shown in the marker's label
         * @param field The field metadata object
         */
        setField(field: IMetadata): void {
            var value = this.record.values[field.key];

            if (value == null) {
                this.label.innerText = "n/a";
            } else {
                this.label.innerText = value;
            }


            if ((field.key === "WindMax" || field.key === "WindMaxDir")) {
                //If showing a wind gust field, make sure the barb reflects the gust values
                if (!this.showingGust) {
                    this.setWind(true);
                }
            }
            else if (this.showingGust) {
                //If not showing a wind gust field, make sure the barb reflects average values
                this.setWind(false);
            }
        }

        /**
         *  Converts MPH to Knots
         * @param mph The miles per hours value
         */
        mphToKnots(mph: number): number {
            return mph * 0.8689762419;
        }

        /** Updates the UI to show the marker as selected */
        select(): void {
            var classes = this.container.className.split(" ");
            if (classes.indexOf("selected") === -1) {
                this.container.className += " selected";
            }
        }

        /** Updates the UI to NOT show the marker as selected */
        deselect(): void {
            this.container.className = this.container.className
                .split(" ")
                .filter(c => c !== "selected")
                .join(" ");
        }

        /** Using the age, determines if the station data shown for this marker is considered out of date */
        isOld(): boolean {
            return this.age > StationMarker.oldAgeCutoff;
        }
    }
}


/** Google Maps information window which shows station record data for a selected station */
export class DetailWindow {
    container: HTMLDivElement;
    map: google.maps.Map;
    metadata: any;
    fields: Array<string>;
    report: MapReport;
    position: google.maps.ControlPosition;

    firstShow: boolean = true;
    currentWindow: HTMLDivElement;
    marker: IStationMarker;

    /**
     * Constructs a new DetailWindow object
     * @param map The google map to add the window to
     * @param position The position on the map the detail window should be inserted
     * @param metadata The field metadata returned from the API
     * @param fields The list of fields (in order) returned from the API
     * @param report The MapReport object
     */
    constructor(map: google.maps.Map, position: google.maps.ControlPosition, metadata: any, fields: Array<string>, report: MapReport) {
        this.map = map;
        this.metadata = metadata;
        this.report = report;
        this.fields = fields;
        this.position = position;

        this.container = document.createElement("div");
        this.container.className = "station-details-container gm-style";

    }

    /**
     * Builds and shows the detail window for the specified marker
     * @param marker The marker object to show the detail window for
     */
    show(marker: IStationMarker) {
        var self = this;
        self.close();

        self.marker = marker;
        let station = marker.station;
        let record = marker.record;
        let isOld = marker.isOld();

        var box = document.createElement("div");
        box.className = isOld
            ? "detail-window detail-window-old"
            : "detail-window";

        var close = document.createElement("span");
        close.className = "fa fa-close";
        close.addEventListener("click", (e: Event) => {
            self.close();
        });
        box.appendChild(close);

        var title = document.createElement("div");
        title.className = "detail-window-title";
        title.innerText = station.name;
        box.appendChild(title);

        var dto = new Date(record.date);

        var date = document.createElement("div");
        date.className = "detail-window-date";
        date.innerText = `${dto.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${dto.toLocaleDateString()}`;
        if (isOld) {
            date.innerHTML = '<span class="fa fa-warning" title="Data may be out of date"></span> ' + date.innerHTML;
        }

        box.appendChild(date);

        var table = document.createElement("table");
        var body = document.createElement("tbody");

        for (let key of this.fields) {
            var field = this.metadata[key];
            if (!field) continue;

            this.addFieldRow(body, record, field);
        }

        table.appendChild(body);
        box.appendChild(table);

        var reportLink = document.createElement("a");
        reportLink.href = `/reports/view?reportType=Tabular&stations=${station.id}&interval=60&fields=${this.fields.join("%2C")}&dateType=1&dateCount=36`;
        reportLink.className = "detail-window-report-link";
        reportLink.target = "_blank";
        reportLink.innerHTML = '<span class="fa fa-table"></span> 36-Hour Tabular Report';
        box.appendChild(reportLink);

        this.container.appendChild(box);
        this.currentWindow = box;

        if (this.firstShow) {
            this.map.controls[this.position].push(this.container);

            var adjust = box.clientWidth / 2 * -1;
            this.map.panBy(adjust, 0);
            this.firstShow = false;

        }
    };

    /**
     * Utility function to add a record field to an html table output
     * @param body The table object
     * @param record The station data record
     * @param field The metadata of the field in the record to add
     */
    private addFieldRow(body: HTMLTableSectionElement, record: IStationRecord, field: IMetadata) {
        var value = record.values[field.key];
        if (value == null) return;

        var row = body.insertRow();
        row.title = field.longName;

        //Label cell
        var cell = row.insertCell();
        cell.innerText = field.shortName;

        //Value cell
        cell = row.insertCell();
        cell.innerHTML = value;
        if (field.units) {
            cell.innerHTML += ` <span class="units">${field.units}</span>`;
        }

        //Direction fields, prepend cardinality
        if (field.interpretation === "Direction") {
            cell.innerHTML = `${MapUtilities.getCardindalDirection(value)} <span class="units">|</span> ${cell.innerHTML}`;
        }

        //Report link cell
        cell = row.insertCell();

        if (field.interpretation === "Numeric") {
            var reportUrl = `/reports/view?reportType=LineChart&stations=${record.stationId}&interval=60&fields=${field.key}&dateType=1&dateCount=36`;
            cell.innerHTML = `<a href="${reportUrl}" target="_blank" title="View ${field.shortName} 36-Hour Line Chart Report"><span class="fa fa-line-chart"></span></a>`;
        }
    }

    /** Closes the detail window */
    close() {
        //remove all content from the container
        this.container.innerHTML = "";
        if (this.marker) {
            this.marker.deselect();
            this.marker = null;
        }
    }


}

/** Custom map controls for the WWG Map Report */
export class CustomMapControls {
    container: HTMLDivElement;
    map: google.maps.Map;
    metadata: any;
    fields: Array<string>;
    report: MapReport;

    selectInput: HTMLSelectElement;
    layerInput: HTMLSelectElement;
    mapTypeInput: HTMLSelectElement;
    aerisOverlay: google.maps.ImageMapType;
    refreshBtn: HTMLButtonElement;

    currentFieldKey: string;
    currentField: IMetadata;


    constructor(map: google.maps.Map, position: google.maps.ControlPosition, metadata: any, fields: Array<string>, report: MapReport) {
        var self = this;
        this.map = map;
        this.metadata = metadata;
        this.fields = fields;
        this.report = report;
        this.container = document.createElement("div");
        this.container.className = "custom-controls-container gm-style";

        this.currentFieldKey = fields[0];
        this.currentField = metadata[this.currentFieldKey];

        this.addFieldSelector();
        this.addMapTypeSelector();
        // WWG wants to wait on this, since the layers don't loop
        //this.addLayerSelector();
        this.addRefreshButton();

        this.map.controls[position].push(this.container);
    }

    /** Add the field select drop-down to the controls container */
    private addFieldSelector() {
        var self = this;
        var select: HTMLSelectElement = this.selectInput = document.createElement("select");
        var selectContainer = document.createElement("div");
        selectContainer.className = "field-selector-container";
        selectContainer.appendChild(select);

        self.updateFieldsInputs();

        select.addEventListener("change", (e: Event) => { self.processSelectedField(); });

        this.container.appendChild(selectContainer);
    }

    setFields(metadata: any, fields: Array<string>): void {
        this.metadata = metadata;
        this.fields = fields;


        if (this.fields.indexOf(this.currentFieldKey) < 0) {
            //If the currently selected field does not exist anymore
            //Switch to the first one in the new data set
            this.currentFieldKey = this.fields[0];
        }

        this.currentField = this.metadata[this.currentFieldKey];

        this.updateFieldsInputs();
    }

    private updateFieldsInputs() {
        var self = this;

        //Remove any existing options
        self.selectInput.innerHTML = "";

        for (let key of self.fields) {
            let field: IMetadata = self.metadata[key];
            if (!field) continue;

            var option: HTMLOptionElement = document.createElement("option");
            option.value = key;
            option.text = field.shortName;
            option.selected = key === self.currentFieldKey;

            if (field.units) {
                option.text += " (" + field.units + ")";
            }

            self.selectInput.appendChild(option);
        }
    }


    /** Gets the currently selected field, and pushes updates accordingly */
    private processSelectedField(): void {

        if (!this.selectInput) return;

        this.currentFieldKey = this.selectInput.options[this.selectInput.selectedIndex].value;
        this.currentField = this.metadata[this.currentFieldKey];

        //Update each existing marker with the newly selected field
        for (let marker of this.report.markers) {
            marker.setField(this.currentField);
        }
    }


    private addMapTypeSelector() {
        var self = this;
        var select: HTMLSelectElement = this.mapTypeInput = document.createElement("select");
        var selectContainer = document.createElement("div");
        selectContainer.className = "map-type-selector-container";
        selectContainer.appendChild(select);

        var addOption = (name: string, value: google.maps.MapTypeId) => {
            var option: HTMLOptionElement = document.createElement("option");
            option.value = value.toString();
            option.text = name;
            option.selected = value === this.map.getMapTypeId();
            select.appendChild(option);
        }

        addOption("Road Map", google.maps.MapTypeId.ROADMAP);
        addOption("Terrain", google.maps.MapTypeId.TERRAIN);
        addOption("Satellite", google.maps.MapTypeId.SATELLITE);
        addOption("Hybrid", google.maps.MapTypeId.HYBRID);

        select.addEventListener("change", (e: Event) => {
            self.map.setMapTypeId(self.mapTypeInput.value);
        });

        this.container.appendChild(selectContainer);
    }


    private addRefreshButton() {
        var self = this;
        var btn: HTMLButtonElement = this.refreshBtn = document.createElement("button");
        btn.className = "btn";
        btn.title = "Refresh Data";

        var btnContainer = document.createElement("div");
        btnContainer.className = "map-refresh-btn";
        btnContainer.appendChild(btn);

        var icon: HTMLSpanElement = document.createElement("span");
        icon.className = "fa fa-refresh";
        btn.appendChild(icon);

        btn.addEventListener("click", (e: Event) => {
            self.report.refreshData();
        });

        this.container.appendChild(btnContainer);
    }


    private addLayerSelector() {
        var self = this;
        var select: HTMLSelectElement = this.layerInput = document.createElement("select");
        var selectContainer = document.createElement("div");
        selectContainer.className = "layer-selector-container";
        selectContainer.appendChild(select);

        var addOption = (name: string, value: string) => {
            var option: HTMLOptionElement = document.createElement("option");
            option.value = value;
            option.text = name;
            select.appendChild(option);
        }

        addOption("No Overlay", null);
        addOption("Radar", "radar:50");
        addOption("Visible Satellite", "satellite-visible:50");
        addOption("Infrared Satellite", "satellite:50");
        addOption("Alerts", "alerts:50");
        addOption("Active Fires", "fires-obs-icons");
        addOption("Stormcells", "stormcells");

        select.addEventListener("change", (e: Event) => {
            var value = self.layerInput.value;
            self.setAerisOverlay(value);
        });

        this.container.appendChild(selectContainer);
    }

    private setAerisOverlay(type: string): void {
        this.map.overlayMapTypes.clear();
        if (!type || type === "") return;

        this.aerisOverlay = this.createAerisLayer(type);
        this.map.overlayMapTypes.push(this.aerisOverlay);
    }

    private createAerisLayer(type: string): google.maps.ImageMapType {
        const id = "p9DrDWwOWMOehu1ocLMrW";
        const key = "yoScmo7d9FG7U4lFN7mhy0VDtIBuMutzq8lmTfUW";

        var overlay = new google.maps.ImageMapType({
            getTileUrl: (coord, zoom) => `https://maps.aerisapi.com/${id}_${key}/${type}/${zoom}/${coord.x}/${coord.y}/current.png`,
            tileSize: new google.maps.Size(256, 256)
        });

        return overlay;
    }
}

/** Enum for used by the SvgBarbGenerator */
enum SvgBarbPieceType {
    None,
    Flag,
    Line,
}


module MapUtilities {
    var directions = [
        "N",
        "NNE",
        "NE",
        "ENE",
        "E",
        "ESE",
        "SE",
        "SSE",
        "S",
        "SSW",
        "SW",
        "WSW",
        "W",
        "WNW",
        "NW",
        "NNW",
        "N"
    ];

    export function getCardindalDirection(degrees: number): string {
        if (degrees < 0 || degrees > 360) return null;
        var sixteenth = Math.ceil((degrees + 11.25) / 22.5);
        return directions[sixteenth - 1];
    }
}

/** Utility to dynamically build up a Wind Barb SVG graphic based on a wind speed */
export class SvgBarbGenerator {
    /**
     * Speed is purposely not in a specific unit. Would generally be knots, but this allows the units to be mph instead
     * if desired. Barb rendering logic is based on the number without the unit (5, 10, 15, etc)
     */
    speed: number;

    private remaining: number;
    private y: number = 0;
    private result: string;
    private last: SvgBarbPieceType;

    constructor(speed: number) {
        this.speed = speed;
    }

    /** Builds up and returns the SVG graphic string for the configured wind speed */
    build(): string {
        this.y = 5;

        if (this.speed === 0) {
            console.log("this barb is calm");
            return SvgBarbGenerator.calm();
        }
        //if (this.speed < 5) return SvgBarbGenerator.calm();

        this.remaining = this.speed;
        this.result = '<svg class="barb" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 105 230"><g>';
        this.last = SvgBarbPieceType.None;

        while (this.remaining >= 5) {
            if (this.remaining >= 50) {
                this.add50();
            }
            else if (this.remaining >= 10) {
                this.add10();
            } else {
                this.add5();

            }
        }

        this.result += '<line class="barb-stroke barb-ln" x1="20" y1="29.3" x2="20" y2="195" />';
        this.result += '<circle class="barb-stroke barb-pt" cx="20" cy="210" r="15"/>';
        this.result += '</g>';

        return this.result;
    }

    /** Returns the SVG string for calm winds */
    public static calm(): string {
        return '<svg class="calm" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 80 80"><g><circle class="barb-stroke barb-pt" cx="40" cy="40" r="30"/><circle class="barb-stroke barb-pt" cx="40" cy="40" r="15"/></g></svg>';
    }

    /** Returns the SVG string for a point with no wind data */
    public static point(): string {
        return '<svg class="point" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 80 80"><g><circle class="barb-stroke barb-pt" cx="40" cy="40" r="15"/></g></svg>';
    }

    /** Adds a 50 flag to the output result, and updates the remaining units value */
    private add50(): void {
        if (this.last === SvgBarbPieceType.Flag) {
            this.y += 28;
        }

        var y = this.y;
        this.result += `<polyline class="barb-fill barb-f50" points="104,${y} 15,${y} 15,${y + 30.4}" />`;

        this.last = SvgBarbPieceType.Flag;
        this.remaining -= 50;
    }

    /** Adds a 10 line to the output result, and updates the remaining units value */
    private add10(): void {
        if (this.last === SvgBarbPieceType.Flag) {
            this.y += 21;
        } else if (this.last === SvgBarbPieceType.Line) {
            this.y += 26;
        }

        var y = this.y;
        this.result += `<line class="barb-stroke barb-l10" x1="17" y1="${y + 29}" x2="102" y2="${y}" />`;

        this.last = SvgBarbPieceType.Line;
        this.remaining -= 10;
    }

    /** Adds a 5 line to the output result, and updates the remaining units value */
    private add5(): void {
        if (this.last === SvgBarbPieceType.None) {
            //If a 5 line is the first item, it needs to start as if its the 2nd
            this.y = 44;
        } else if (this.last === SvgBarbPieceType.Flag) {
            this.y += 34;
        } else if (this.last === SvgBarbPieceType.Line) {
            this.y += 39;
        }

        var y = this.y;
        this.result += `<line class="barb-stroke barb-l5" x1="17" y1="${y + 16}" x2="64" y2="${y}" />`;

        this.last = SvgBarbPieceType.Line;
        this.remaining -= 5;
    }

}